import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import Webphone from 'common/Webphone'
import { DefaultKavkomIntegration } from 'common/Globals'
import { setContactsModalVisible, setTransferModalVisible, setKeypadModalVisible } from 'store/actions/global'
import { callTo, transfer } from 'store/actions/call'
import { CancelCallButton } from 'components/pages/dialpad/components/CancelCallButton'
import { ActionCallButton } from 'components/pages/dialpad/components/ActionCallButton'
import { ReactComponent as KeypadIcon } from 'assets/svg/dialpad/keypad.svg'
import { CallAvatar } from './components/CallAvatar'
import { CallInfo } from './components/CallInfo'
import CallActions from './components/CallActions'
import { CallDirection } from 'common/Enums'
import { CallKeyboardActions } from 'store/models/CallPreferences'
import { TransportState } from 'sip.js'
import { CallerInsightsPopup } from 'components/shared'
import { useTranslation } from 'react-i18next'
import './Call.scss'
import { AppUrls } from 'common/AppUrls'

export function Call() {
    const dispatch = useDispatch()
    const history = useHistory()

    let { number: numberRouteParam } = useParams()

    const [callInfoNumbers, setCallInfoNumbers] = useState([])
    const [currentCall, setCurrentCall] = useState(undefined)
    const [recordingDisabled, setRecordingDisabled] = useState(false)
    const [recordingDomainsWhitelist, setRecordingDomainsWhitelist] = useState([])
    const lastRecordingSendTimeRef = useRef(0)
    const RECORDING_SEND_DEBOUNCE_MS = 500

    // Store data
    const { calls } = useSelector((state) => state.call)
    const { transportStatus } = useSelector((state) => state.userAgent)

    const { isOnTransfer, enteredPhoneNumber } = useSelector((state) => state.call)
    const { callPreferences = [], volume, selectedConfigIds } = useSelector((state) => state.settings)
    const { data: profileData, integration } = useSelector((state) => state.profile)

    const holdCall = calls.find((item) => item.number !== enteredPhoneNumber)

    useEffect(() => {
        if (numberRouteParam && transportStatus === TransportState.Connected) {
            dispatch(callTo(numberRouteParam))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numberRouteParam, transportStatus, history])

    useEffect(() => {
        setCurrentCall(
            calls.find((item) => {
                const tryingOutbound = item.isTrying && item.direction === CallDirection.Outgoing
                return item.isActive || tryingOutbound
            })
        )
    }, [calls])

    useEffect(() => {
        if (!currentCall) {
            setRecordingDisabled(false)
        }
    }, [currentCall])

    useEffect(() => {
        fetch('/recording-domains-whitelist.json')
            .then((res) => res.json())
            .then((domains) => setRecordingDomainsWhitelist(Array.isArray(domains) ? domains : []))
            .catch(() => setRecordingDomainsWhitelist([]))
    }, [])

    const domainName = profileData?.domain_name || ''
    const isRecordingAllowed = recordingDomainsWhitelist.includes(domainName)

    const audio = useMemo(() => {
        const audio = new Audio('/audio/outgoing-beeps.mp3')
        audio.volume = 0.01 * volume
        audio.loop = true

        if (selectedConfigIds.speakersCallDeviceId && typeof audio.setSinkId === 'function') {
            audio
                .setSinkId(selectedConfigIds.speakersCallDeviceId)
                .then()
                .catch((error) => console.error(error))
        }
        return audio
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let callback

        if (currentCall && currentCall.isCalling) {
            if (enteredPhoneNumber.length) {
                const playPromise = audio.play()

                playPromise.then(() => {
                    callback = () => audio.pause()
                })
            }
        }

        return () => {
            callback && callback()
        }
    }, [audio, currentCall, enteredPhoneNumber])

    useEffect(() => {
        if (audio) {
            audio.volume = 0.01 * volume
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volume])

    // Actions
    const onClickCancel = () => currentCall && Webphone.hangup(currentCall.number)
    const onHoldHandler = () => currentCall && Webphone.toggleHold(currentCall.number)
    const onMuteMicHandler = () => currentCall && Webphone.toggleMute(currentCall.number)
    const onSwapHandler = () => currentCall && Webphone.swap(currentCall.number)
    const onTransferHandler = () => dispatch(setTransferModalVisible(true))
    const onAddHandler = () => dispatch(setContactsModalVisible(true))
    const onMergeHandler = () => Webphone.merge(currentCall.number)
    const onKeypadHandler = () => dispatch(setKeypadModalVisible(true))
    const onConfirmTransferHandler = () => {
        let holdCallNumber
        
        if (holdCall?.number === currentCall?.number) {
            const hold = calls.find((item) => item.isHold)
            holdCallNumber = hold.number
        } else {
            holdCallNumber = holdCall?.number
        }

        dispatch(
            transfer({
                transferingNumber: holdCallNumber,
                transferToNumber: currentCall.number
            })
        )
    }
    const onManageConferenceHandler = () => history.push(AppUrls.conference)

    const onRecordingToggleHandler = () => {
        if (!currentCall) return
        const now = Date.now()
        if (now - lastRecordingSendTimeRef.current < RECORDING_SEND_DEBOUNCE_MS) return
        lastRecordingSendTimeRef.current = now

        const number = currentCall.number
        if (recordingDisabled) {
            Webphone.dtmf(number, '*6')
            setRecordingDisabled(false)
        } else {
            Webphone.dtmf(number, '*5')
            setRecordingDisabled(true)
        }
    }

    useEffect(() => {
        let isHungUpActionEnabled = !!callPreferences.find((item) => item.type === CallKeyboardActions.hangup)
            ?.isChecked

        function onKeyPress(event) {
            const keyName = event.key
            if (isHungUpActionEnabled && keyName === 'Escape') {
                Webphone.hangup(currentCall.number)
            }
        }

        if (isHungUpActionEnabled) {
            document.addEventListener('keyup', onKeyPress)

            return () => {
                document.removeEventListener('keyup', onKeyPress)
            }
        }
    }, [callPreferences, currentCall])

    useEffect(() => {
        const inboundTryingFilter = (item) => {
            const incomingTrying = item.direction === CallDirection.Incoming && item.isTrying
            const incomingCalling = item.direction === CallDirection.Incoming && item.isCalling
            return !incomingTrying && !incomingCalling && !item.isMinimized
        }
        setCallInfoNumbers(calls.filter(inboundTryingFilter))
    }, [calls])

    const { searchOnlyKavkomCRM } = useSelector((state) => state.settings)
    const { t } = useTranslation()
    const isCallDisabled = currentCall && (currentCall.isFinished || currentCall.isCalling || currentCall.isTrying)

    return (
        <>
            <div className="call d-flex flex-column align-items-center">
                <CallAvatar />
                <CallInfo {...{ callInfoNumbers, currentCall }} />
                <CallActions
                    {...{
                        calls,
                        currentCall,
                        isOnTransfer,
                        onHoldHandler,
                        onMuteMicHandler,
                        onSwapHandler,
                        onTransferHandler,
                        onAddHandler,
                        onMergeHandler,
                        onConfirmTransferHandler,
                        onManageConferenceHandler,
                        onRecordingToggleHandler,
                        onKeypadHandler,
                        recordingDisabled,
                        showRecordingButton: isRecordingAllowed
                    }}
                />
                <div className="call__actions w-100 mt-0">
                  <div className="call__actions--row d-flex justify-content-between align-items-center mb-4">
                    {isRecordingAllowed ? (
                        <ActionCallButton
                            action={onKeypadHandler}
                            icon={KeypadIcon}
                            iconClass='mt-2'
                            text={t('keypad')}
                            disabled={isCallDisabled}
                            iconHeight={19}
                        />
                    ) : (
                        <button type="button" className="call__action-btn" aria-hidden="true" />
                    )}
                    <CancelCallButton onClick={onClickCancel} />
                    <button type="button" className="call__action-btn"></button>
                    </div>
                </div>
            </div>

            {currentCall?.callerInsights && (
                <CallerInsightsPopup
                    number={currentCall.number}
                    insights={currentCall.callerInsights}
                    integration={searchOnlyKavkomCRM ? DefaultKavkomIntegration : integration}
                    collapsable={true}
                    className="absolute--bottom"
                    showRecordsInTitle={true}
                />
            )}
        </>
    )
}
